import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://mytableflow.com/api';

// Create axios instance for JSON requests
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: false
});

// Create separate axios instance for file uploads (without Content-Type header)
const apiFileUpload = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    // Don't set Content-Type for FormData - let browser set it with boundary
  },
});

// DEFINE handleResponseError FIRST (before using it)
const handleResponseError = (error) => {
  if (error.response?.status === 401) {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    window.location.href = '/login';
  }
  return Promise.reject(error.response?.data || error);
};

// Request interceptors to add auth token
const addAuthToken = (config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
};

api.interceptors.request.use(addAuthToken);
apiFileUpload.interceptors.request.use(addAuthToken);

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => response.data,
  handleResponseError
);

apiFileUpload.interceptors.response.use(
  (response) => response.data,
  handleResponseError
);

// Auth API
export const authAPI = {
  registerRestaurant: (data) => api.post('/auth.php', {
    action: 'register_restaurant',
    ...data
  }),

  loginRestaurant: (data) => api.post('/auth.php', {
    action: 'login_restaurant',
    ...data
  }),

  registerUser: (data) => api.post('/auth.php', {
    action: 'register_user',
    ...data
  }),

  loginUser: (data) => api.post('/auth.php', {
    action: 'login_user',
    ...data
  }),
  updateProfile: (data) => api.post('/auth.php', {
    action: 'update_profile',
    ...data
  })
};

// Menu API
export const menuAPI = {
  getCategories: () => api.get('/menu.php/categories'),
  createCategory: (data) => api.post('/menu.php/categories', data),
  updateCategory: (data) => api.put('/menu.php/categories', data),
  deleteCategory: (data) => api.delete('/menu.php/categories', { data }),
  getDishes: (params = {}) => api.get('/menu.php/dishes', { params }),
  createDish: (data) => api.post('/menu.php/dishes', data),
  updateDish: (data) => api.put('/menu.php/dishes', data),
  deleteDish: (data) => api.delete('/menu.php/dishes', { data }),
  saveCategoryOrder: (categoryOrder) => {
    return api.post('/update-category-order.php', {
      category_order: categoryOrder
    });
  },
  getCategoryOrder: () => {
    return api.get('/get-category-order.php');
  },


  // Image upload methods
  uploadImage: (file) => {
    const formData = new FormData();
    formData.append('image', file);
    formData.append('action', 'upload_image');

    return apiFileUpload.post('/upload.php', formData);
  },
};

// Tables API 
export const tablesAPI = {
  getTables: () => api.get('/tables.php'),
  createTable: (data) => api.post('/tables.php', data),
  updateTable: (data) => api.put('/tables.php', data),
  deleteTable: (data) => api.delete('/tables.php', { data }),
  getTableByQrCode: (qrCode) => api.get(`/tables.php?qr_code=${qrCode}`),
};

// Orders API 
export const ordersAPI = { 
  getOrders: (params = {}) => api.get('/orders.php', { params }),
  getOrderById: (id) => api.get(`/orders.php?id=${id}`),
  createOrder: (data) => api.post('/orders.php/create', data),
  addOrderItem: (data) => api.post('/orders.php/add-item', data),
  updateOrderStatus: (data) => api.put('/orders.php/update-status', data),
  updatePaymentStatus: (data) => api.put('/orders.php/update-payment', data),
  deleteOrder: (data) => api.delete('/orders.php', { data }),
  cancelOrder: (id) => api.put('/orders.php/cancel', { id: id }),
  getSuggestionAnalytics: (restaurantId, period = 'today') => {
    return api.get(`/orders.php/suggestion-analytics?restaurant_id=${restaurantId}&period=${period}`);
  }
};

// Customer API
export const customerAPI = {
  getTableInfo: (qrCode) => api.get(`/customer.php/table?qr_code=${qrCode}`),
  getMenu: (qrCode) => api.get(`/customer.php/menu?qr_code=${qrCode}`),
  createOrder: (data) => api.post('/customer.php/order', data),
  getOrders: async (qrCode) => {
    const response = await api.get(`/customer.php/order/${qrCode}`);
    return response.data;
  },
  trackSuggestionStats: (data) => {
    return api.post('/customer.php/suggestion-stats', {
      action: 'track_suggestion_stats',
      ...data
    });
  },
  getSuggestionStats: (restaurantId) => {
    return api.get(`/customer.php/suggestion-stats?restaurant_id=${restaurantId}`);
  },
  getCategoryOrder: (restaurantId) => api.get(`/get-category-order.php?restaurant_id=${restaurantId}`),
  cancelOrder: (data) => api.put('/customer.php/cancel-order', data)
};

// Staff API
export const staffAPI = {
  getStaff: () => api.get('/staff.php'),
  createStaff: (data) => api.post('/staff.php', data),
  updateStaff: (data) => api.put('/staff.php', data),
  deleteStaff: (data) => api.delete('/staff.php', { data }),
};

// Subscription API
export const subscriptionAPI = {
  getSubscription: () => api.get('/subscription.php'),
  getPlans: () => api.post('/subscription.php', { action: 'get_plans' }),
  createCheckoutSession: (data) => api.post('/checkout-session.php', {
    action: 'create_checkout_session',
    ...data
  }),
  createBillingPortalSession: () => api.post('/checkout-session.php', {
    action: 'create_billing_portal_session'
  }),
  getTrialStatus: () => api.post('/subscription.php', {
    action: 'get_trial_status'
  }),
  activateTrial: () => api.post('/subscription.php', {
    action: 'activate_trial'
  }),
  checkStaffLimits: (restaurantId) => api.get(`/check-staff-limits.php?restaurant_id=${restaurantId}`),
  syncSubscription: () => api.post('/subscription.php', {
    action: 'sync'
  }),
  handleCheckoutSuccess: (sessionId) => api.post('/subscription.php', {
    action: 'checkout_success',
    session_id: sessionId
  }),
  cancelSubscription: () => api.post('/subscription.php', {
    action: 'cancel'
  }),
};

export default api;